import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { Layout } from '../../../shared/components'
import categoryService, { Category } from '../../../services/categoryService'
import VehicleSearchWidget from '../../../components/VehicleSearchWidget'
import { buildImageUrl } from '../../../config'

const CSS_HANDLES = [
  'catalogSelection',
  'catalogSelectionHeader',
  'catalogSelectionTitle',
  'catalogSelectionSubtitle',
  'catalogSelectionContent',
  'catalogSelectionGrid',
  'categoryCard',
  'categoryCardHeader',
  'categoryCardTitle',
  'categoryCardDescription',
  'categoryCardFooter',
  'categoryCardImage',
  'featureCard',
  'featureCardTitle',
  'featureCardSubtitle',
  'loadingState',
  'errorState'
] as const

const CATEGORY_METADATA: Record<string, { description: string, color: string, icon: React.ReactNode }> = {
  'carro': {
    description: 'Energia brasileira presente nas maiores montadoras do mundo.',
    color: 'bg-slate-100',
    icon: (
      <svg className="w-48 h-32 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
        <path d="M7 10h10l1 3H6l1-3zM2 13h20v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4zM5 13V9l1-2h12l1 2v4M5 17a1 1 0 100 2 1 1 0 000-2zm14 0a1 1 0 100 2 1 1 0 000-2z" />
      </svg>
    )
  },
  'onibus-e-caminhao': {
    description: 'Resistência para rodar mais tempo na estrada.',
    color: 'bg-slate-100',
    icon: (
      <svg className="w-48 h-32 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
        <path d="M3 6h12v11H3V6zM15 10h6l1 2v5h-7v-7zM6 17a2 2 0 100 4 2 2 0 000-4zm12 0a2 2 0 100 4 2 2 0 000-4zM15 6h4v4h-4V6z" />
      </svg>
    )
  },
  'moto': {
    description: 'Energia para sua moto ir mais longe.',
    color: 'bg-slate-100',
    icon: (
      <svg className="w-48 h-32 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
        <circle cx="5" cy="18" r="3" />
        <circle cx="19" cy="18" r="3" />
        <path d="M12 18V9l4-2h4v2l-4 2h-4v7h-3z" />
        <path d="M8 18l4-4h4" />
      </svg>
    )
  },
  'nautica': {
    description: 'A bateria exclusiva para embarcações.',
    color: 'bg-slate-100',
    icon: (
      <svg className="w-48 h-32 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
        <path d="M22 10l-1 2H3l-1-2h20zM3 12l1 7h16l1-7H3zM12 2v8M9 5l3-3 3 3" />
      </svg>
    )
  }
}

const CatalogSelection: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const navigate = useNavigate()
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true)
        setError(null)
        const categoriesList = await categoryService.list()
        setCategories(categoriesList)
      } catch (err) {
        console.error('Erro ao carregar categorias:', err)
        setError('Não foi possível carregar as categorias. Tente novamente mais tarde.')
      } finally {
        setLoading(false)
      }
    }

    void loadCategories()
  }, [])

  const handleCategoryClick = (categorySlug: string) => {
    navigate(`/catalogo/${categorySlug}`)
  }

  const renderSkeleton = () => (
    <Layout>
      <div className={`${handles.catalogSelection} min-h-screen bg-white`}>
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex overflow-x-auto lg:grid lg:grid-cols-4 pb-8 lg:pb-0 gap-4 lg:gap-6 snap-x">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-72 lg:w-full h-[480px] rounded-2xl bg-gray-100 animate-pulse"
              />
            ))}
          </div>
        </div>
      </div>
    </Layout>
  )

  if (loading) {
    return renderSkeleton()
  }

  if (error) {
    return (
      <Layout>
        <div className={`${handles.catalogSelection} min-h-screen bg-gray-50`}>
          <div className="max-w-7xl mx-auto px-4 py-12">
            <div className={`${handles.errorState} bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg text-center`}>
              <p className="font-medium">{error}</p>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className={`${handles.catalogSelection} min-h-screen bg-white overflow-hidden`}>
        {/* Content */}
        <div className={`${handles.catalogSelectionContent} max-w-7xl mx-auto px-4 py-12`}>
          {/* Busca Inteligente - Encontre sua bateria */}
          <div className={`${handles.featureCard} mb-8`}>
            <div className="rounded-2xl overflow-hidden">
              <VehicleSearchWidget />
            </div>
          </div>

          <div className={`${handles.catalogSelectionGrid} flex overflow-x-auto lg:grid lg:grid-cols-2 xl:grid-cols-4 pb-8 lg:pb-0 gap-4 lg:gap-6 snap-x no-scrollbar lg:overflow-visible`}>
            {/* Category Cards */}
            {categories.map((category) => {
              const meta = CATEGORY_METADATA[category.slug] || {
                description: category.description || 'Confira nossas opções de baterias.',
                color: 'bg-slate-50',
                icon: (
                  <svg className="w-24 h-24 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                )
              }
              const displayImage = category.imageUrl
                ? (buildImageUrl(category.imageUrl) ?? category.imageUrl)
                : null

              return (
                <div
                  key={category.id}
                  className={`${handles.categoryCard} flex-shrink-0 w-72 lg:w-full h-[480px] rounded-2xl ${meta.color} p-8 flex flex-col items-center text-center snap-start transition-transform hover:-translate-y-1 cursor-pointer group overflow-hidden`}
                  onClick={() => handleCategoryClick(category.slug)}
                >
                  <div className={`${handles.categoryCardHeader} mb-4`}>
                    <h3 className={`${handles.categoryCardTitle} text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors`}>
                      {category.name}
                    </h3>
                    <p className={`${handles.categoryCardDescription} text-gray-600 text-base leading-relaxed`}>
                      {meta.description}
                    </p>
                  </div>
                  
                  <div className={`${handles.categoryCardFooter} mt-auto flex items-center justify-center w-full flex-1 min-h-0`}>
                    <div className={`${handles.categoryCardImage} transition-all duration-300 group-hover:scale-105 w-full h-full flex items-center justify-center`}>
                      {displayImage ? (
                        <img
                          src={displayImage}
                          alt={category.name}
                          className="max-h-56 w-full max-w-full object-contain grayscale transition-all duration-300 group-hover:grayscale-0"
                        />
                      ) : (
                        <span className="grayscale transition-all duration-300 group-hover:grayscale-0">{meta.icon}</span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <style dangerouslySetInnerHTML={{ __html: `
            .no-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .no-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
          `}} />
        </div>
      </div>
    </Layout>
  )
}

export default CatalogSelection


